import { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { CheckboxGroup } from '@components/checkbox-group';
import { DatePicker } from '@components/datepicker';
import { Header } from '@components/header';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDate } from '@components/icons/date';
import { IconDropdown } from '@components/icons/dropdown';
import { IconPortal } from '@components/icons/portal';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuList } from '@components/menu-list';
import { MultiSegmentedFilters, SegmentConfig } from '@components/multi-segmented-filters';
import { PageContent } from '@components/page-containers/page-container';
import { usePopoverScreens } from '@components/popover-screens/popover-screens';
import { Separator } from '@components/separator';
import { Shapes } from '@components/shapes';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate, isSameDay } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import { usePortals, usePortalStatistics } from 'api/portals/hooks';
import { FormDataPoint, GroupByOption, LinkDataPoint, Portal } from 'api/portals/requests';

import { usePortalFilters } from './hooks/use-portal-filters';
import { usePortalSelection } from './hooks/use-portal-selection';
import { formatDatePortalStatistics, getEndOfDay, getStartOfDay } from './utils';

const BarChart = lazy(() => import('@components/bar-chart/bar-chart'));

export const PortalStatistics = () => {
  const { t } = useScopedTranslate(locale);
  const { data: portals, isLoading: isLoadingPortals } = usePortals();

  const {
    selectedPortalIds,
    setSelectedPortalIds,
    selectedDateFilter,
    selectedGroupBy,
    setSelectedGroupBy,
    customDateRange,
    handleDateFilterChange,
    handleCustomDateRangeChange,
    statisticsFilters,
  } = usePortalFilters();

  const { selectedPortalNames, handleCheckboxGroupChange } = usePortalSelection(
    portals,
    selectedPortalIds,
    setSelectedPortalIds
  );

  const [lastSuccessfulData, setLastSuccessfulData] = useState<typeof analyticsData>(undefined);
  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(undefined);
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    // case 1: both temp dates selected (new range or changed both dates)
    if (tempStartDate && tempEndDate) {
      handleCustomDateRangeChange({
        startDate: getStartOfDay(tempStartDate).toISOString(),
        endDate: getEndOfDay(tempEndDate).toISOString(),
      });
      handleDateFilterChange('chooseADateRange');
      setTempStartDate(undefined);
      setTempEndDate(undefined);
    }
    // case 2: only start date changed, keep existing end date
    else if (
      tempStartDate &&
      !tempEndDate &&
      customDateRange.endDate &&
      selectedDateFilter === 'chooseADateRange'
    ) {
      handleCustomDateRangeChange({
        startDate: getStartOfDay(tempStartDate).toISOString(),
        endDate: customDateRange.endDate,
      });
      setTempStartDate(undefined);
    }
    // case 3: only end date changed, keep existing start date
    else if (
      tempEndDate &&
      !tempStartDate &&
      customDateRange.startDate &&
      selectedDateFilter === 'chooseADateRange'
    ) {
      handleCustomDateRangeChange({
        startDate: customDateRange.startDate,
        endDate: getEndOfDay(tempEndDate).toISOString(),
      });
      setTempEndDate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tempStartDate,
    tempEndDate,
    customDateRange,
    selectedDateFilter,
    handleDateFilterChange,
    handleCustomDateRangeChange,
  ]);

  const { data: analyticsData } = usePortalStatistics(statisticsFilters);

  useEffect(() => {
    if (analyticsData) {
      setLastSuccessfulData(analyticsData);
    }
  }, [analyticsData]);

  const getDateFilterButtonText = () => {
    if (selectedDateFilter === 'dateToNow' && customDateRange.startDate) {
      const startDate = abbreviateDate(
        new Date(customDateRange.startDate),
        'en',
        'date-fancy-short'
      );
      return `${startDate} - ${t('now')}`;
    }

    if (
      selectedDateFilter === 'chooseADateRange' &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      const startDate = abbreviateDate(
        new Date(customDateRange.startDate),
        'en',
        'date-fancy-short'
      );

      const endDate = new Date(customDateRange.endDate);
      const endDateText = isSameDay(endDate, new Date())
        ? t('now')
        : abbreviateDate(endDate, 'en', 'date-fancy-short');

      return `${startDate} - ${endDateText}`;
    }

    return t(selectedDateFilter);
  };

  const {
    popover: dateFilterPopover,
    setCurrentScreen,
    reset,
  } = usePopoverScreens({
    placement: 'bottom-start',
    trigger: (
      <Button
        size="sm"
        text={getDateFilterButtonText()}
        variant="secondary"
        iconLeft={<IconDate />}
        iconRight={<IconDropdown />}
      />
    ),
    initialScreen: 'menu',
    screens: {
      menu: (
        <ScreenContainer>
          <MenuList
            titleFont="regular14"
            items={[
              {
                title: t('today'),
                onSelect: () => {
                  handleDateFilterChange('today');
                  reset();
                },
              },
              {
                title: t('last7Days'),
                onSelect: () => {
                  handleDateFilterChange('last7Days');
                  reset();
                },
              },
              {
                title: t('last30Days'),
                onSelect: () => {
                  handleDateFilterChange('last30Days');
                  reset();
                },
              },
              {
                title: t('last90Days'),
                onSelect: () => {
                  handleDateFilterChange('last90Days');
                  reset();
                },
              },
              {
                title: t('pastYear'),
                onSelect: () => {
                  handleDateFilterChange('pastYear');
                  reset();
                },
              },
              {
                title: t('dateToNow'),
                onSelect: () => setCurrentScreen('dateToNow'),
              },
              {
                title: t('chooseADateRange'),
                onSelect: () => {
                  setTempStartDate(undefined);
                  setTempEndDate(undefined);
                  setCurrentScreen('dateRange');
                },
              },
            ]}
          />
        </ScreenContainer>
      ),
      dateToNow: (
        <ScreenContainer>
          <Stack space={2}>
            <Stack orientation="horizontal" space={2} align="center">
              <IconButton
                icon={<IconArrowLeft />}
                variant="ghost-purple"
                onClick={() => setCurrentScreen('menu')}
              />
              <Text text={t('dateToNow')} font="semibold14" />
            </Stack>
            <DatePicker
              label={t('startDate')}
              placeholder={t('selectStartDate')}
              value={
                selectedDateFilter === 'dateToNow' && customDateRange.startDate
                  ? new Date(customDateRange.startDate)
                  : undefined
              }
              onSelect={(date: Date) => {
                if (date && !isNaN(date.getTime())) {
                  handleCustomDateRangeChange({ startDate: date.toISOString() });
                  handleDateFilterChange('dateToNow');
                  reset();
                }
              }}
            />
          </Stack>
        </ScreenContainer>
      ),
      dateRange: (
        <ScreenContainer>
          <Stack space={2}>
            <Stack orientation="horizontal" space={2} align="center">
              <IconButton
                icon={<IconArrowLeft />}
                variant="ghost-purple"
                onClick={() => setCurrentScreen('menu')}
              />
              <Text text={t('chooseADateRange')} font="semibold14" />
            </Stack>
            <DatePicker
              label={t('startDate')}
              placeholder={t('selectStartDate')}
              value={
                tempStartDate ||
                (selectedDateFilter === 'chooseADateRange' && customDateRange.startDate
                  ? new Date(customDateRange.startDate)
                  : undefined)
              }
              onSelect={(date: Date) => {
                if (date && !isNaN(date.getTime())) {
                  setTempStartDate(date);
                  if (tempEndDate) {
                    setTempEndDate(undefined);
                  }
                }
              }}
            />

            <DatePicker
              label={t('endDate')}
              placeholder={t('selectEndDate')}
              value={
                tempEndDate ||
                (selectedDateFilter === 'chooseADateRange' && customDateRange.endDate
                  ? new Date(customDateRange.endDate)
                  : undefined)
              }
              onSelect={(date: Date) => {
                if (date && !isNaN(date.getTime())) {
                  setTempEndDate(date);
                }
              }}
            />
          </Stack>
        </ScreenContainer>
      ),
    },
  });

  if (isLoadingPortals || !portals) {
    return <Spinner />;
  }

  const displayData = analyticsData || lastSuccessfulData;
  const metrics = [
    { title: t('externalForms'), value: displayData?.totalForms ?? 0 },
    {
      title: t('views'),
      value: displayData?.totalViews ?? 0,
      delta: displayData?.viewsDelta,
    },
    {
      title: t('submissions'),
      value: displayData?.totalSubmissions ?? 0,
      delta: displayData?.submissionsDelta,
    },
    { title: t('customLinks'), value: displayData?.totalLinks ?? 0 },
    {
      title: t('clicks'),
      value: displayData?.totalClicks ?? 0,
      delta: displayData?.clicksDelta,
    },
  ];

  return (
    <>
      <Header breadcrumbs={<Breadcrumb items={[{ label: t('portalStatistics') }]} />} />
      <ControlActions
        selectedPortalNames={selectedPortalNames}
        setSelectedPortalIds={setSelectedPortalIds}
        handleCheckboxGroupChange={handleCheckboxGroupChange}
        portals={portals}
        dateFilterPopover={dateFilterPopover}
        selectedGroupBy={selectedGroupBy}
        handleGroupBySelect={setSelectedGroupBy}
      />
      <PageContent>
        <Stack space={6}>
          <Stack orientation="horizontal" space={0}>
            {metrics.map((metric, index, arr) => {
              return (
                <Card
                  key={metric.title}
                  data={metric}
                  isFirst={index === 0}
                  isLast={index === arr.length - 1}
                />
              );
            })}
          </Stack>
          <AnalyticsBarChart
            data={displayData?.formData ?? []}
            groupBy={selectedGroupBy}
            title={t('externalForms')}
            barDefinitions={[
              {
                dataKey: 'views',
                color: 'elementOrange100',
                title: t('views'),
              },
              {
                dataKey: 'submissions',
                color: 'elementGreen100',
                title: t('submissions'),
              },
            ]}
          />
          <AnalyticsBarChart
            data={displayData?.linkData ?? []}
            groupBy={selectedGroupBy}
            title={t('customLinks')}
            barDefinitions={[
              {
                dataKey: 'clicks',
                color: 'elementBlue100',
                title: t('clicks'),
              },
            ]}
          />
        </Stack>
      </PageContent>
    </>
  );
};

const ControlActions = ({
  selectedPortalNames,
  setSelectedPortalIds,
  handleCheckboxGroupChange,
  portals,
  dateFilterPopover,
  selectedGroupBy,
  handleGroupBySelect,
}: {
  selectedPortalNames: string[];
  setSelectedPortalIds: (value: React.SetStateAction<string[]>) => void;
  handleCheckboxGroupChange: (selectedNames: string[]) => void;
  portals: Portal[];
  dateFilterPopover: React.ReactNode;
  selectedGroupBy: GroupByOption;
  handleGroupBySelect: (groupBy: GroupByOption) => void;
}) => {
  const { t } = useScopedTranslate(locale);

  const selectedText =
    selectedPortalNames.length === 1
      ? selectedPortalNames[0]
      : t('selected', { count: selectedPortalNames.length });

  const portalMenuActions = (
    <CheckboxGroup value={selectedPortalNames} onChange={handleCheckboxGroupChange}>
      {portals.map((portal) => (
        <CheckboxGroup.Item key={portal.shortId} label={portal.name} value={portal.name} />
      ))}
    </CheckboxGroup>
  );

  const segments: SegmentConfig[] = [
    {
      id: 'portal-label',
      icon: <IconPortal color="buttonIconPositive" />,
      text: t('portal'),
      menuActions: portalMenuActions,
    },
    {
      id: 'comparator',
      text: t('isComparator'),
      textColor: 'elementGreen100',
    },
    {
      id: 'selected-value',
      menuActions: portalMenuActions,
      text: selectedText,
      textColor: 'elementBlue100',
      style: { whiteSpace: 'normal' },
      textStyles: {
        ...lineClamp('-webkit-box', 1, true),
      },
    },
    {
      id: 'close',
      icon: <IconCloseRegular color="buttonIconPositive" />,
      onClick: () => {
        setSelectedPortalIds([]);
      },
    },
  ];

  const trigger = selectedPortalNames.length > 0 ? MultiSegmentedFilters : Button;
  const triggerProps =
    selectedPortalNames.length > 0
      ? { segments }
      : {
          size: 'sm' as const,
          text: t('allPortals'),
          variant: 'secondary' as const,
          iconLeft: <IconPortal />,
          iconRight: <IconDropdown />,
        };

  return (
    <Controls>
      <Stack
        orientation="horizontal"
        space={2}
        align="center"
        style={{
          minWidth: 0,
          whiteSpace: 'nowrap',
        }}
      >
        <Menu placement="bottom-start" trigger={trigger} triggerProps={triggerProps}>
          {portalMenuActions}
        </Menu>

        <Separator orientation="horizontal" />
        {dateFilterPopover}
        <Text
          text={t('groupedBy')}
          font="regular14"
          color="textSecondary"
          style={{ whiteSpace: 'nowrap' }}
        />
        <Menu
          trigger={Button}
          triggerProps={{
            size: 'sm',
            text: t(selectedGroupBy),
            variant: 'secondary',
            iconRight: <IconDropdown />,
          }}
        >
          <Menu.Item label={t('day')} onSelect={() => handleGroupBySelect('day')} />
          <Menu.Item label={t('week')} onSelect={() => handleGroupBySelect('week')} />
          <Menu.Item label={t('month')} onSelect={() => handleGroupBySelect('month')} />
          <Menu.Item label={t('entireRange')} onSelect={() => handleGroupBySelect('entireRange')} />
        </Menu>
      </Stack>
    </Controls>
  );
};

type MetricCardData = {
  title: string;
  value: number;
  delta?: number;
};

const Card = ({
  data,
  isFirst = false,
  isLast = false,
}: {
  data: MetricCardData;
  isFirst?: boolean;
  isLast?: boolean;
}) => {
  const { title, value, delta } = data;
  const isPositive = delta !== undefined && delta > 0;
  const displayText = delta !== undefined ? `${formatNumber(Math.abs(delta))}%` : '';
  const iconColor = isPositive ? 'green100' : 'red100';
  const iconTransform = isPositive ? 'rotate(180deg)' : 'rotate(0deg)';

  return (
    <Stack
      space={2}
      style={{
        padding: `${pxToRem(16)} ${pxToRem(24)}`,
        backgroundColor: theme.color.backgroundLightDark,
        width: '100%',
        height: pxToRem(100),
        border: `${pxToRem(1)} solid ${theme.color.purpleTrans10}`,
        borderRadius: isFirst
          ? `${pxToRem(8)} 0 0 ${pxToRem(8)}`
          : isLast
          ? `0 ${pxToRem(8)} ${pxToRem(8)} 0`
          : '0',
        ...(!isLast && {
          borderRight: 'none',
        }),
      }}
    >
      <Text text={title} font="bold14" color="textSecondary" caps />
      <Stack
        space={1}
        orientation="horizontal"
        justify="space-between"
        align="center"
        style={{ height: pxToRem(38) }}
      >
        <Text text={formatNumber(value)} font="display1" />
        {delta !== undefined && (
          <Stack orientation="horizontal" align="center">
            <Text text={displayText} font="bold16" color="textSecondary" />
            <Stack
              style={{
                transform: iconTransform,
              }}
            >
              {delta === 0 ? (
                <Shapes.Circle color="orange100" size={12} style={{ marginLeft: pxToRem(8) }} />
              ) : (
                <IconDropdown color={iconColor} />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

type ChartDataPoint = {
  day: string;
  date: string;
  clicks: number;
  views?: number;
  submissions?: number;
};

const AnalyticsBarChart = ({
  data,
  groupBy,
  title,
  barDefinitions,
}: {
  data: FormDataPoint[] | LinkDataPoint[];
  groupBy: GroupByOption;
  title: string;
  barDefinitions: Array<{
    dataKey: keyof ChartDataPoint;
    color: Color;
    title: string;
  }>;
}) => {
  const chartData: ChartDataPoint[] = useMemo(
    () =>
      data.map((item: FormDataPoint | LinkDataPoint) => ({
        day: formatDatePortalStatistics(item.date, groupBy),
        ...item,
      })),
    [data, groupBy]
  );

  return (
    <Stack
      orientation="horizontal"
      style={{
        backgroundColor: theme.color.backgroundLightDark,
        borderRadius: pxToRem(8),
        border: `${pxToRem(1)} solid ${theme.color.border10}`,
      }}
    >
      <Suspense fallback={null}>
        <BarChart
          data={chartData}
          category="day"
          bars={barDefinitions}
          height={250}
          title={title}
          showTooltip
          tooltipLabelKey="day"
        />
      </Suspense>
    </Stack>
  );
};

const Controls = styled(Stack)({
  padding: `${pxToRem(8)} ${pxToRem(24)}`,
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
  minWidth: 0,
  minHeight: pxToRem(48),
});

const ScreenContainer = styled.div({
  padding: pxToRem(16),
  minWidth: pxToRem(300),
});

export const locale = {
  en: {
    portalStatistics: 'Portal statistics',
    portal: 'Portal',
    groupedBy: 'Grouped by',
    noPortals: 'No portals found',
    today: 'Today',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    pastYear: 'Past year',
    dateToNow: 'Date to now',
    chooseADateRange: 'Choose a date range',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    entireRange: 'Entire range',
    externalForms: 'External forms',
    views: 'Views',
    submissions: 'Submissions',
    clicks: 'Clicks',
    customLinks: 'Custom links',
    startDate: 'Start date',
    selectStartDate: 'Select start date',
    endDate: 'End date',
    selectEndDate: 'Select end date',
    isComparator: 'is',
    selected: '{count} selected',
    portals: 'Portals',
    allPortals: 'All portals',
    now: 'Now',
  },
};
